import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import Textfield from '@atlaskit/textfield';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  products?: string;
  otherAtlassianProducts?: string;
};

type Props = {
  values: FormValues;
  updateMultiSelect: (key: keyof FormValues, optionId: string) => void;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

const PRODUCT_OPTIONS = [
  { id: 'ai_rovo', label: 'Atlassian Intelligence/Rovo' },
  { id: 'atlassian_guard', label: 'Atlassian Guard' },
  { id: 'compass', label: 'Compass' },
  { id: 'confluence', label: 'Confluence' },
  { id: 'enterprise', label: 'Enterprise' },
  { id: 'forge', label: 'Forge' },
  { id: 'jira', label: 'Jira' },
  { id: 'jira_align', label: 'Jira Align' },
  { id: 'jira_product_discovery', label: 'Jira Product Discovery' },
  { id: 'jira_service_management', label: 'Jira Service Management' },
  { id: 'loom', label: 'Loom' },
  { id: 'marketplace_apps', label: 'Marketplace apps' },
  { id: 'trello', label: 'Trello' },
  { id: 'other', label: 'Other (please specify)' },
];

export const ProductsSection: React.FC<Props> = ({ values, updateMultiSelect, updateField, errors }) => {
  const toggleProduct = (id: string) => () => updateMultiSelect('products', id);
  const showOtherInput = values.products.includes('other');
  const hasProductsError = !!errors?.products;
  const fieldClassName = hasProductsError
    ? 'champion-form-products champion-form-products--error'
    : 'champion-form-products';

  return (
    <div css={sectionBoxStyles}>
      <div css={[fieldStyles, { marginBottom: 0 }]} className={fieldClassName}>
        <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>{' '}
          Which Atlassian apps do you use? Select all that apply.
        </label>

        <div css={controlTopStyles}>
          {PRODUCT_OPTIONS.map((opt) => (
            <Checkbox
              key={opt.id}
              name="productsused"
              label={opt.label}
              isChecked={values.products.includes(opt.id)}
              onChange={toggleProduct(opt.id)}
            />
          ))}

          {errors?.products && <div css={errorStyles}>{errors.products}</div>}
        </div>
      </div>

      {showOtherInput && (
        <div css={fieldTopStyles}>
          <label css={[labelStyles, salesforceLabelStyles]} htmlFor="other-atlassian-products">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>{' '}
            Please specify
          </label>
          <Textfield
            id="other-atlassian-products"
            value={values.otherAtlassianProducts}
            onChange={(e) => updateField('otherAtlassianProducts', (e.target as HTMLInputElement).value)}
            isInvalid={!!errors?.otherAtlassianProducts}
            isCompact
          />
          {errors?.otherAtlassianProducts && <div css={errorStyles}>{errors.otherAtlassianProducts}</div>}
        </div>
      )}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '1rem',
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const labelBoldStyles = css({
  fontWeight: 600,
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '1rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
